import { useState } from "react";
import {
  EyeIcon,
  EyeSlashIcon,
  ExclamationCircleIcon,
  SignalIcon,
} from "@heroicons/react/24/outline";
import { useLanguage } from "../../../contexts/LanguageContext";

// Small building blocks for the integrations list and drawer.

export const inputClass =
  "block w-full min-h-11 rounded-[10px] border border-gray-300 bg-white px-3 text-sm text-gray-900 shadow-none placeholder:text-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 read-only:bg-gray-50";

export const outlineButtonClass =
  "inline-flex min-h-10 items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3.5 text-[13px] font-semibold text-gray-900 hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-60";

export function Field({ label, htmlFor, hint, children, action }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={htmlFor} className="text-[13px] font-semibold text-gray-700">
          {label}
        </label>
        {action}
      </div>
      {children}
      {hint && <p className="text-xs leading-normal text-gray-600">{hint}</p>}
    </div>
  );
}

export function SecretInput({ id, value, onChange, readOnly, required, placeholder, className = "" }) {
  const { t } = useLanguage();
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        type={shown ? "text" : "password"}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        required={required}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        className={`${inputClass} pr-11 ${className}`}
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? t("integrations.fields.hide") : t("integrations.fields.show")}
        aria-pressed={shown}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-500 hover:text-gray-700"
      >
        {shown ? (
          <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
        ) : (
          <EyeIcon className="h-5 w-5" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

// A labelled on/off row, bordered, as in the redesign.
export function SwitchRow({ id, title, description, checked, onChange, children }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 px-4 py-3.5">
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 flex-col gap-0.5">
          <span id={`${id}-label`} className="text-sm font-semibold text-gray-900">
            {title}
          </span>
          {description && (
            <span className="text-xs leading-normal text-gray-600">{description}</span>
          )}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-labelledby={`${id}-label`}
          onClick={() => onChange(!checked)}
          className={`relative h-[26px] w-11 shrink-0 rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
            checked ? "bg-indigo-600" : "bg-gray-300"
          }`}
        >
          <span
            aria-hidden="true"
            className={`absolute top-[3px] h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
              checked ? "left-[21px]" : "left-[3px]"
            }`}
          />
        </button>
      </div>
      {children}
    </div>
  );
}

export function Section({ title, description, children }) {
  return (
    <div className="flex flex-col gap-2.5 rounded-xl border border-gray-200 px-4 py-3.5">
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold text-gray-900">{title}</span>
        {description && (
          <span className="text-xs leading-normal text-gray-600">{description}</span>
        )}
      </span>
      {children}
    </div>
  );
}

export function ErrorBox({ lines }) {
  if (!lines?.length) return null;
  return (
    <div role="alert" className="flex gap-3 rounded-[10px] bg-red-50 p-3.5 text-sm text-red-800">
      <ExclamationCircleIcon className="h-5 w-5 shrink-0 text-red-500" aria-hidden="true" />
      {lines.length === 1 ? (
        <p>{lines[0]}</p>
      ) : (
        <ul className="list-disc space-y-1 pl-4">
          {lines.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// The provider logos are 2:1 wordmarks (media.loyaltify.io, 300×150), so the
// tile is 2:1 too: in a square they shrank to about 32×16 and could not be
// read. `size` is the tile's height; the width is twice that.
export function ProviderLogo({ provider, size = 44 }) {
  const style = { width: size * 2, height: size };
  if (provider.key === "webhook") {
    return (
      <span
        aria-hidden="true"
        style={style}
        className="flex shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700"
      >
        <SignalIcon className="h-1/2 w-auto" />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      style={style}
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white p-1"
    >
      <img
        src={`https://media.loyaltify.io/static/integrations/${provider.key}.png`}
        alt=""
        className="max-h-full max-w-full object-contain"
      />
    </span>
  );
}

const DELIVERY_TONES = {
  success: "bg-emerald-50 text-emerald-800",
  failed: "bg-red-50 text-red-800",
  pending: "bg-amber-50 text-amber-800",
};

export function DeliveryStatusPill({ status }) {
  const { t } = useLanguage();
  const known = ["success", "failed", "pending"].includes(status);
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${
        DELIVERY_TONES[status] || "bg-gray-100 text-gray-700"
      }`}
    >
      {known ? t(`integrations.deliveries.status.${status}`) : status}
    </span>
  );
}
